import React, { useMemo } from "react";
import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";

import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  composeE164,
  countryByCode,
  flagFor,
} from "../../phoneCountries";

// Un teléfono = país + número nacional. Se parte en dos controles a propósito:
// mientras el indicativo se tecleaba a mano, un número extranjero sólo entraba
// bien si quien lo escribía sabía que había que anteponer "+"; sin el "+",
// toE164 le encajaba el indicativo de Colombia y guardaba un número que no
// existe. Con el país escogido aparte, el indicativo deja de ser algo que el
// usuario pueda olvidar.
//
// El componente es CONTROLADO y no guarda nada: recibe `countryCode` y
// `national` (lo tecleado, sin indicativo) y devuelve los dos más el E.164 ya
// compuesto, que es lo único que se guarda de un celular.
//
// Props:
//   countryCode / national     estado, del padre
//   onChange({ countryCode, national, e164 })
//   label, placeholder, helperText, error, disabled, autoFocus, required
//   nationalLabel              etiqueta del número (por defecto la de `label`)

export default function PhoneNumberField({
  countryCode = DEFAULT_PHONE_COUNTRY,
  national = "",
  onChange,
  label = "Teléfono",
  placeholder = "300 111 2233",
  helperText,
  error = false,
  disabled = false,
  autoFocus = false,
  required = false,
}) {
  const country = useMemo(
    () => countryByCode(countryCode) || countryByCode(DEFAULT_PHONE_COUNTRY),
    [countryCode]
  );

  const emit = (nextCountry, nextNational) =>
    onChange?.({
      countryCode: nextCountry,
      national: nextNational,
      e164: composeE164(nextCountry, nextNational),
    });

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
      <Autocomplete
        disabled={disabled}
        options={PHONE_COUNTRIES}
        value={country}
        disableClearable
        // Se puede buscar por nombre o por indicativo: quien sabe que su país es
        // "+51" no tiene por qué recordar cómo lo escribimos nosotros.
        getOptionLabel={(o) => `${o.name} (${o.dial})`}
        isOptionEqualToValue={(o, v) => o.code === v.code}
        filterOptions={(options, { inputValue }) => {
          const q = inputValue.trim().toLowerCase();
          if (!q) return options;
          return options.filter(
            (o) =>
              o.name.toLowerCase().includes(q) ||
              o.dial.includes(q.replace(/^\+?/, "+")) ||
              o.code.toLowerCase() === q
          );
        }}
        onChange={(_, v) => v && emit(v.code, national)}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.code}>
            <Typography component="span" sx={{ mr: 1 }}>{flagFor(o.code)}</Typography>
            <Typography component="span" variant="body2" sx={{ flexGrow: 1 }}>{o.name}</Typography>
            <Typography component="span" variant="caption" color="text.secondary">{o.dial}</Typography>
          </Box>
        )}
        sx={{ width: { xs: "100%", sm: 230 }, flexShrink: 0 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="País"
            // El helper del número ya ocupa esa línea; sin este espacio los dos
            // controles quedan desalineados en cuanto uno de los dos lo muestre.
            helperText={helperText ? " " : undefined}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <Typography component="span" sx={{ ml: 0.5, mr: 0.25 }}>
                  {flagFor(country.code)}
                </Typography>
              ),
            }}
          />
        )}
      />

      <TextField
        fullWidth
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        type="tel"
        label={label}
        value={national}
        onChange={(e) => emit(country.code, e.target.value)}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        // El indicativo se muestra pegado al campo —y no sólo dentro del
        // selector— para que se vea el número completo que se va a guardar.
        InputProps={{
          startAdornment: (
            <Typography component="span" color="text.secondary" sx={{ mr: 0.75 }}>
              {country.dial}
            </Typography>
          ),
        }}
      />
    </Stack>
  );
}
